import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { API_BASE_URL } from '../../lib/config';

interface ConsensusAnalysisProps {
  season: number;
}

interface ConsensusGame {
  game_id: number;
  week: number;
  home_team: string;
  away_team: string;
  home_team_abbr: string;
  away_team_abbr: string;
  consensus_pick: string;
  result: string;
  pick_count: number;
  users: string[];
}

interface ConsensusRecord {
  wins: number;
  losses: number;
  pushes: number;
  total: number;
}

interface ConsensusLevel {
  record: ConsensusRecord;
  games: ConsensusGame[];
}

interface ConsensusData {
  '4/4': ConsensusLevel;
  '3/4': ConsensusLevel;
}

const ConsensusAnalysis: React.FC<ConsensusAnalysisProps> = ({ season }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<ConsensusData | null>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/analysis/consensus-picks/${season}`);

      if (!response.ok) {
        throw new Error('Failed to fetch consensus data');
      }

      const result = await response.json();
      setData(result.consensus_levels);
    } catch (err: any) {
      setError(err.message || 'Failed to load consensus data');
    } finally {
      setLoading(false);
    }
  };

  const getResultChip = (result: string) => {
    const colorMap: { [key: string]: 'success' | 'error' | 'default' } = {
      win: 'success',
      loss: 'error',
      push: 'default'
    };
    return (
      <Chip
        label={result.toUpperCase()}
        color={colorMap[result] || 'default'}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Alert severity="info">No consensus data available for this season yet.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Wisdom of the Crowd
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        When everyone (or almost everyone) agrees on a pick, how often is the crowd right?
      </Typography>

      {Object.entries(data).map(([level, consensusData]) => {
        const record = consensusData.record;
        const winRate = record.total > 0 ? ((record.wins / record.total) * 100).toFixed(1) : '0.0';

        return (
          <Paper key={level} elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {level} Agreement ({record.total} games)
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {record.wins}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Wins
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {record.losses}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Losses
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                <Typography variant="h4" color="text.secondary">
                  {record.pushes}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pushes
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main">
                  {winRate}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Win Rate
                </Typography>
              </Box>
            </Box>

            {consensusData.games.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>View All Games ({consensusData.games.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Week</TableCell>
                          <TableCell>Matchup</TableCell>
                          <TableCell>Consensus Pick</TableCell>
                          <TableCell>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {consensusData.games
                          .sort((a: ConsensusGame, b: ConsensusGame) => a.week - b.week)
                          .map((game: ConsensusGame, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{game.week}</TableCell>
                              <TableCell>
                                {game.away_team_abbr} @ {game.home_team_abbr}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={game.consensus_pick === 'home' ? game.home_team_abbr : game.away_team_abbr}
                                  color="primary"
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{getResultChip(game.result)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default ConsensusAnalysis;
